import { isAssignedOverlay } from '@/components/Packaging/typeGuards';
import { isBodySegmentKind, isOpenTeaseOrCreditsKind } from '@/components/Packaging/utils';
import { OVERLAY_KIND, OverlayLayer, SegmentItem } from '@/redux/slices/packagingSlice.types';
import { nanoid } from 'nanoid';

export interface Timeline {
  segments: SegmentItem[];
  overlays: OverlayLayer[];
}

/*
 * # How the template application works
 *
 * When a new template is applied to the packaging timeline, the following rules apply:
 *
 * ## Overlays
 * All overlays on the timeline are replaced by those defined in the new template.
 * Exception: if the timeline already has a rating card with media assigned, all rating card overlays from the template
 * are ignored and the existing rating card is kept as-is. The existing rating card is placed in the first overlay
 * layer; remaining template overlay layers (with rating cards stripped) follow after it.
 *
 * ## Segments
 * The set, order, and structure of segments always comes from the new template.
 *
 * ### Body and Body with Credits segments
 * For Body and Body with Credits segments, the assigned media from the existing timeline is carried
 * over into the matching slot in the new template. Each existing body segment fills exactly one slot:
 * - If the timeline has fewer body segments than the template, remaining slots keep whatever is in the template.
 * - If the timeline has more body segments than the template, excess segments are discarded.
 *
 * ### Open/Tease and all Credits variants segments
 * For Open/Tease and all Credits variants, template media takes precedence over
 * existing timeline media. If the template slot has no media, the existing timeline media is kept.
 * If neither has media, the slot remains empty.
 *
 * ### Other segment kinds
 * All other non-body segments are taken entirely from the new template.
 *
 * After the template is applied, all body segments are super locked — the user cannot unlock them.
 */
export const applyTemplateToTimeline = (timeline: Timeline, template: Timeline): Timeline => {
  const withSourcesTransferred = applySegmentAndOverlayRules(timeline, template);
  const withBodySegmentsLocked = lockBodySegments(withSourcesTransferred);
  return withBodySegmentsLocked;
};

/**
 * Transfers sources from the timeline's body segments to the body segment of the applied template.
 * @param timeline state of the timeline before template swap
 * @param template the template that is being applied
 */
const applySegmentAndOverlayRules = (timeline: Timeline, template: Timeline) => {
  // clone data because we're going to mutate it
  timeline = structuredClone(timeline);
  template = structuredClone(template);

  let prevSegmentEnd = 0;

  return {
    segments: template.segments.map((templateSegment) => {
      let timelineBodySegment: SegmentItem | undefined;
      let timelineBodySegmentIndex: number | undefined;

      if (isBodySegmentKind(templateSegment.kind)) {
        timelineBodySegmentIndex = timeline.segments.findIndex((segment) => segment.kind === templateSegment.kind);
        if (timelineBodySegmentIndex !== -1) {
          timelineBodySegment = timeline.segments[timelineBodySegmentIndex];
        }
      }

      let duration = templateSegment.end - templateSegment.start;

      // transfer sources from body segments
      if (timelineBodySegment?.source) {
        templateSegment.source = timelineBodySegment.source;
        templateSegment.subtitle = timelineBodySegment.subtitle;
        duration = timelineBodySegment.end - timelineBodySegment.start;

        // Every matching segment should only be used once; after we use it, we remove it from the list
        if (typeof timelineBodySegmentIndex !== 'undefined') timeline.segments.splice(timelineBodySegmentIndex, 1);
      }

      // For Open/Tease and Credits: if template has no source, fall back to timeline source
      if (isOpenTeaseOrCreditsKind(templateSegment.kind) && !templateSegment.source) {
        const fallbackSegment = timeline.segments.find((s) => s.kind === templateSegment.kind && s.source);
        if (fallbackSegment) {
          templateSegment.source = fallbackSegment.source;
          duration = fallbackSegment.end - fallbackSegment.start;
          templateSegment.subtitle = fallbackSegment.subtitle;
        }
      }

      // Since we may assign sources to segments, we need to recalculate their positions
      templateSegment.start = prevSegmentEnd;
      templateSegment.end = prevSegmentEnd + duration;
      prevSegmentEnd = templateSegment.end;
      templateSegment.draggableId = nanoid();
      return templateSegment;
    }),
    overlays: buildResultOverlays(timeline.overlays, template.overlays)
  };
};

const buildResultOverlays = (timelineOverlays: OverlayLayer[], templateOverlays: OverlayLayer[]): OverlayLayer[] => {
  const timelineRatingCard = timelineOverlays.flat().find((o) => o.kind === OVERLAY_KIND.RATING_CARD && isAssignedOverlay(o));

  if (!timelineRatingCard) {
    return templateOverlays;
  }

  const filteredTemplateLayers = templateOverlays
    .map((layer) => layer.filter((o) => o.kind !== OVERLAY_KIND.RATING_CARD))
    .filter((layer) => layer.length > 0);

  return [[timelineRatingCard], ...filteredTemplateLayers];
};

const lockBodySegments = (timeline: Timeline): Timeline => ({
  ...timeline,
  segments: timeline.segments.map((segment) => {
    if (isBodySegmentKind(segment.kind)) {
      return {
        ...segment,
        isLocked: true,
        isSuperLocked: true
      };
    }

    return segment;
  })
});
